import React from "react";

/**
 * SituationGuide — guía rápida de las 7 situaciones de cierre y campos clave.
 * Objetivo: que quien revisa/edita no ensucie la data (pedido de Copec).
 * Overlay propio (por encima del detalle y de los modales) + versión imprimible.
 *
 * Props: onClose: () => void
 */

const SITUATIONS = [
  {
    n: "1", name: "Simulacro / Prueba de sistema", result: "Simulacro", color: "#a855f7",
    use: "Activación coordinada para probar el sistema o entrenar al equipo.",
    avoid: "No usarla para descartar un evento real que no se gestionó (usa 2).",
  },
  {
    n: "2", name: "Guardia no asistió", result: "Sin gestión", color: "#ef4444",
    use: "Nadie fue a la zona: sin respuesta del guardia o sin despacho.",
    avoid: "No usarla si el guardia fue pero llegó tarde (usa 3, 4 o 5 según lo encontrado).",
  },
  {
    n: "3", name: "Asistió, señal ya había cesado", result: "Sin hallazgo", color: "#64748b",
    use: "El guardia llegó a la zona y la señal ya había terminado; sin nada que reportar.",
    avoid: "No usarla si al acercarse la señal seguía activa (usa 4 o 5).",
  },
  {
    n: "4", name: "Señal se perdió al acercarse", result: "Con hallazgo", color: "#22c55e",
    use: "La señal cesó justo cuando el guardia se aproximaba — disuasión probable.",
    avoid: "No usarla si la señal nunca estuvo activa durante la aproximación (usa 3).",
  },
  {
    n: "5", name: "Señal persistente, sin localizar", result: "Con hallazgo", color: "#22c55e",
    use: "La señal siguió activa con el guardia en la zona, pero no se ubicó la fuente.",
    avoid: "No usarla si se identificó a una persona (usa 6) o una fuente conocida (usa 7).",
  },
  {
    n: "6", name: "Sospechoso identificado", result: "Con hallazgo", color: "#22c55e",
    use: "Se vio a una persona vinculada a la señal. Completa patente y descripción si aplica.",
    avoid: "No usarla si solo se encontró un vehículo sin ocupantes (usa 4 o 5 y registra la patente).",
  },
  {
    n: "7", name: "Fuente conocida / ambiental", result: "Con hallazgo", color: "#22c55e",
    use: "Se identificó una fuente legítima o ambiental que explica la señal (equipos, antenas, clima).",
    avoid: "No usarla como comodín cuando no se encontró nada (eso es 5).",
  },
];

const FIELDS = [
  ["Patente", "Formato AABB11. Regístrala siempre que haya vehículo, aunque la situación no sea la 6."],
  ["Sospechoso", "Género, edad aproximada, vestimenta y dirección de huida. Sin juicios: solo lo observado."],
  ["Barras de señal", "Las que marcó el detector al momento de la gestión (1–6). No estimar de memoria."],
  ["Comentarios", "Obligatorios en hallazgos: qué se encontró, acciones tomadas, contacto con autoridades."],
];

export default function SituationGuide({ onClose }) {
  return (
    <div
      onClick={onClose}
      className="situation-guide-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 10050,
        background: "rgba(2,6,17,0.82)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="situation-guide-card"
        style={{
          width: "100%", maxWidth: 680, maxHeight: "88vh", overflowY: "auto",
          background: "var(--card)", border: "1px solid var(--border)",
          borderRadius: 12, padding: 18,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 4 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "var(--bright)" }}>Guía rápida de cierre de eventos</div>
            <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
              Qué significa cada situación y cuándo NO usarla — para mantener la data limpia.
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }} className="no-print">
            <button
              type="button"
              onClick={() => window.print()}
              style={{
                padding: "5px 10px", borderRadius: 6, border: "1px solid var(--border)",
                background: "var(--surface)", color: "var(--subtle)", fontSize: 10, fontWeight: 700, cursor: "pointer",
              }}
            >🖨️ Imprimir</button>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "5px 10px", borderRadius: 6, border: "1px solid var(--border)",
                background: "var(--surface)", color: "var(--subtle)", fontSize: 10, fontWeight: 700, cursor: "pointer",
              }}
            >✕ Cerrar</button>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
          {SITUATIONS.map((s) => (
            <div key={s.n} style={{
              background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8,
              padding: "9px 11px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  minWidth: 18, height: 18, borderRadius: 5, fontSize: 10, fontWeight: 800,
                  background: `${s.color}22`, color: s.color, border: `1px solid ${s.color}55`,
                }}>{s.n}</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--bright)" }}>{s.name}</span>
                <span style={{
                  marginLeft: "auto", fontSize: 8.5, fontWeight: 700, padding: "2px 7px",
                  borderRadius: 5, background: `${s.color}1a`, color: s.color, border: `1px solid ${s.color}40`,
                  whiteSpace: "nowrap",
                }}>Resultado: {s.result}</span>
              </div>
              <div style={{ fontSize: 10.5, color: "var(--subtle)", marginTop: 5, lineHeight: 1.45 }}>{s.use}</div>
              <div style={{ fontSize: 10, color: "#fbbf24", marginTop: 3, lineHeight: 1.45 }}>⚠ {s.avoid}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em", margin: "14px 0 6px" }}>
          Campos que alimentan los reportes
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 6 }}>
          {FIELDS.map(([k, v]) => (
            <div key={k} style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 10px" }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "var(--subtle)" }}>{k}</div>
              <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2, lineHeight: 1.45 }}>{v}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 12 }}>
          Tank Mission Control · Guía de cierre y revisión de eventos · imprime esta guía para la sala CCTV.
        </div>
      </div>

      {/* Estilos de impresión: solo la guía, en claro */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .situation-guide-overlay, .situation-guide-overlay * { visibility: visible !important; }
          .situation-guide-overlay { position: static !important; background: #fff !important; padding: 0 !important; backdrop-filter: none !important; }
          .situation-guide-card { max-height: none !important; overflow: visible !important; border: none !important; background: #fff !important; }
          .situation-guide-card * { color: #111 !important; background: #fff !important; border-color: #bbb !important; }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}
